import {Injectable, inject} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {TrajetDto} from '../dto/trajet.dto';
import {Observable} from 'rxjs';
import {ModeNavette} from '../models/mode-navette.enum';
import {ReservationTrajetDto} from '../dto/reservation-trajet.dto';

@Injectable({
	providedIn: 'root'
})
export class TrajetService {

	private url = '/ws/trajets';

	private http = inject(HttpClient);

	constructor() {
	}

	/**
	 * Retourne l'URL complète pour les réservations d'un trajet.
	 *
	 * @param {number} trajetId l'id du trajet.
	 * @return {string} l'URL complète.
	 */
	private getUrlReservation(trajetId: number): string {
		return this.url + '/' + trajetId + '/reservations';
	}

	/**
	 * Créer un nouveau trajet.
	 *
	 * @param {TrajetDto} trajetDto le trajet à créer.
	 * @returns {Observable<void>} un Observable indiquant la réussite de l'opération.
	 */
	creerTrajet(trajetDto: TrajetDto): Observable<void> {
		return this.http.post<void>(this.url, trajetDto);
	}

	/**
	 * Récupère les trajets en fonction de la différence de nombre de semaines et de jours par rapport à la semaine actuelle.
	 * Si la différence en jours n'est pas renseignée, récupère les trajets pour la semaine entière sinon seulement le jour.
	 *
	 * @param differenceNombreSemaines le nombre de semaines de différence par rapport à la semaine actuelle.
	 * @param differenceNombreJours le nombre de jours de différence par rapport au jour actuel.
	 * @param modeNavette le mode de l'écran de navette afin d'adapter les trajets récupérés.
	 * @returns {Observable<TrajetDto[]>} un Observable contenant la liste des trajets de la semaine recherchée.
	 */
	recupererTrajets(differenceNombreSemaines: number, differenceNombreJours: number | null, modeNavette: ModeNavette): Observable<TrajetDto[]> {
		let params = new HttpParams()
			.set('use-case', modeNavette.toLowerCase())
			.set('differenceNombreSemaines', differenceNombreSemaines.toString());
		if (differenceNombreJours != null) {
			params = params.set('differenceNombreJours', differenceNombreJours.toString());
		}
		return this.http.get<TrajetDto[]>(this.url, { params });
	}

	/**
	 * Met à jour un trajet existant.
	 *
	 * @param {TrajetDto} trajet le trajet à modifier
	 * @returns {Observable<void>} un Observable indiquant la réussite de l'opération.
	 */
	mettreAJourTrajet(trajet: TrajetDto): Observable<void> {
		return this.http.put<void>(`${this.url}/${trajet.id}`, trajet);
	}

	/**
	 * Annule un trajet existant.
	 *
	 * @param trajetId l'identifiant du trajet à annuler.
	 * @returns {Observable<void>} un Observable indiquant la réussite de l'opération.
	 */
	annulerTrajet(trajetId: number): Observable<void> {
		return this.http.delete<void>(`${this.url}/${trajetId}`);
	}

	/**
	 * Réserve un trajet pour l'utilisateur connecté.
	 *
	 * @param {number} trajetId l'id du trajet à réserver.
	 * @return {Observable<void>} un Observable indiquant la réussite de l'opération.
	 */
	reserverTrajet(trajetId: number): Observable<void> {
		return this.http.post<void>(this.getUrlReservation(trajetId), null);
	}

	/**
	 * Annule la réservation d'un trajet pour l'utilisateur connecté.
	 *
	 * @param {number} trajetId l'id du trajet pour lequel la réservation doit être annulée.
	 * @param {number} reservationId l'id de la réservation à annuler.
	 * @return {Observable<void>} un Observable indiquant la réussite de l'opération.
	 */
	annulerReservationTrajet(trajetId: number, reservationId: number): Observable<void> {
		return this.http.delete<void>(`${this.getUrlReservation(trajetId)}/${reservationId}`);
	}

	/**
	 * Récupère les réservations d'un trajet.
	 *
	 * @param {number} trajetId l'id du trajet pour lequel récupérer les réservations.
	 * @return {Observable<ReservationTrajetDto[]>} un Observable contenant la liste des réservations du trajet.
	 */
	recupererReservationsTrajet(trajetId: number): Observable<ReservationTrajetDto[]> {
		return this.http.get<ReservationTrajetDto[]>(this.getUrlReservation(trajetId));
	}
}
